import React from 'react';
import type { TemplateDefinition, LogoPosition } from '../../types/outreach';

interface LogoGridProps {
  logos: NonNullable<TemplateDefinition['branding']>['logos'];
  /** Which area to render: 'header' or 'footer' */
  area: 'header' | 'footer';
  className?: string;
}

const POSITIONS: Record<'header' | 'footer', { left: LogoPosition; center: LogoPosition; right: LogoPosition }> = {
  header: { left: 'header_left', center: 'header_center', right: 'header_right' },
  footer: { left: 'footer_left', center: 'footer_center', right: 'footer_right' }
};

const JUSTIFY: Record<string, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end'
};

const LogoGrid: React.FC<LogoGridProps> = ({ logos, area, className = '' }) => {
  if (!logos) return null;

  const positions = POSITIONS[area];
  const entries: { key: string; logo: typeof logos.organization }[] = [
    { key: 'organization', logo: logos.organization },
    { key: 'workspace', logo: logos.workspace }
  ];

  const hasAnyLogo = (['left', 'center', 'right'] as const).some(slot =>
    entries.some(({ logo }) =>
      logo?.enabled && logo.url && logo.position === positions[slot]
    )
  );

  if (!hasAnyLogo) return null;

  return (
    <div className={`grid grid-cols-3 items-center ${className}`}>
      {(['left', 'center', 'right'] as const).map((slot) => (
        <div key={slot} className={`flex ${JUSTIFY[slot]}`}>
          {entries.map(({ key, logo }) =>
            logo?.enabled && logo.url && (
              logo.position === positions[slot] || (slot === 'left' && logo.position.startsWith(positions[slot]))
            ) ? (
              <img
                key={key}
                src={logo.url}
                alt={`${key === 'organization' ? 'Organization' : 'Workspace'} Logo`}
                className="max-h-20 w-auto object-contain"
              />
            ) : null
          )}
        </div>
      ))}
    </div>
  );
};

export default LogoGrid;
